/*
  component: fot-widget
  title: Sandboxed Widget Embed
  props:
    - widget-id: widget ID to load
    - instance-id: layout section id
    - owner-user-id: profile owner user id
  deps: none
*/
import { getWidgetHostDomain } from '../utils/widget-iframe-host.js';
import { ensureLoaded } from '../utils/domain-blocklist-cache.js';
import { openExternalUrl } from '../utils/open-external-url.js';

class FotWidget extends HTMLElement {
  constructor() {
    super();
    this.port = null;
    this.onMessageHandler = null;
    this.pendingFetches = new Map();
    this._flagStatus = null;
    this._revealed = false;
  }

  static get observedAttributes() {
    return ['widget-id', 'instance-id', 'owner-user-id'];
  }

  connectedCallback() {
    ensureLoaded();
    this.render();
    this.loadWidget();
  }

  disconnectedCallback() {
    this.cleanupChannel();
  }

  get widgetId() {
    return this.getAttribute('widget-id');
  }

  get instanceId() {
    return this.getAttribute('instance-id');
  }

  get ownerUserId() {
    return this.getAttribute('owner-user-id');
  }

  get widgetHostDomain() {
    return getWidgetHostDomain();
  }

  cleanupChannel() {
    if (this.port && this.onMessageHandler) {
      this.port.removeEventListener('message', this.onMessageHandler);
      this.port.close();
    }
    this.port = null;
    this.onMessageHandler = null;
    this.pendingFetches.clear();
  }

  async fetchFlagStatus() {
    const ownerId = this.ownerUserId;
    const sectionId = this.instanceId;
    const widgetId = this.widgetId;
    if (!ownerId || !sectionId || !widgetId) return null;

    try {
      const params = new URLSearchParams({
        widgetId: String(widgetId),
        profileOwnerUserId: String(ownerId),
        sectionId: String(sectionId)
      });
      const res = await fetch(`/api/widgets/flag-status?${params}`, { credentials: 'include' });
      if (!res.ok) return null;
      return res.json();
    } catch (err) {
      window.logger?.warn('Widget flag status check failed:', err);
      return null;
    }
  }

  async handlePortMessage(event, iframe) {
    const data = event.data || {};
    const { type, requestId, height } = data;

    const reply = (payload) => {
      if (!this.port) return;
      this.port.postMessage(payload);
    };

    const replyError = (code, message) => {
      if (!requestId) return;
      reply({ type: 'FOT.ERROR', requestId, code, message });
    };

    if (type === 'FOT.READY') {
      this.querySelector('.fot-widget-loading')?.remove();
      return;
    }

    if (type === 'FOT.RESIZE' && height) {
      iframe.style.height = `${Math.max(80, Number(height))}px`;
      return;
    }

    if (type === 'FOT.OPEN_EXTERNAL') {
      openExternalUrl(data.url);
      return;
    }

    if (type === 'FOT.RENDER_MARKDOWN') {
      try {
        const ownerId = this.ownerUserId;
        const sectionId = this.instanceId;
        if (!ownerId || !sectionId) {
          replyError('PREVIEW_UNSUPPORTED', 'Markdown render requires profile widget instance');
          return;
        }
        const res = await fetch(
          `/api/widget-instances/${encodeURIComponent(ownerId)}/${encodeURIComponent(sectionId)}/render-markdown`,
          { credentials: 'include' }
        );
        const json = await res.json();
        if (!res.ok) {
          replyError(json.code || 'UPSTREAM_ERROR', json.error || 'Markdown render failed');
          return;
        }
        reply({ type: 'FOT.RENDER_MARKDOWN_RESPONSE', requestId, html: json.html || '' });
      } catch (err) {
        window.logger?.error('FOT.RENDER_MARKDOWN failed:', err);
        replyError('UPSTREAM_ERROR', 'Markdown render failed');
      }
      return;
    }

    if (!requestId) return;

    if (type === 'FOT.GET_INSTANCE_CONFIG') {
      try {
        const ownerId = this.ownerUserId;
        const sectionId = this.instanceId;
        if (!ownerId || !sectionId) {
          replyError('NOT_CONFIGURED', 'Widget instance context missing');
          return;
        }
        const res = await fetch(
          `/api/widget-instances/${encodeURIComponent(ownerId)}/${encodeURIComponent(sectionId)}/config`,
          { credentials: 'include' }
        );
        if (!res.ok) {
          replyError('FORBIDDEN', 'Could not load config');
          return;
        }
        const json = await res.json();
        reply({ type: 'FOT.INSTANCE_CONFIG', requestId, config: json.config || {} });
      } catch (err) {
        window.logger?.error('FOT.GET_INSTANCE_CONFIG failed:', err);
        replyError('UPSTREAM_ERROR', 'Config request failed');
      }
      return;
    }

    if (type === 'FOT.FETCH') {
      try {
        const ownerId = this.ownerUserId;
        const sectionId = this.instanceId;
        if (!ownerId || !sectionId) {
          replyError('PREVIEW_UNSUPPORTED', 'Fetch requires profile widget instance');
          return;
        }
        const res = await fetch(
          `/api/widget-instances/${encodeURIComponent(ownerId)}/${encodeURIComponent(sectionId)}/fetch`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ url: data.url, init: data.init })
          }
        );
        const json = await res.json();
        if (!res.ok) {
          replyError(json.code || 'UPSTREAM_ERROR', json.error || 'Fetch failed');
          return;
        }
        reply({
          type: 'FOT.FETCH_RESPONSE',
          requestId,
          ok: json.ok,
          status: json.status,
          headers: json.headers,
          body: json.body
        });
      } catch (err) {
        window.logger?.error('FOT.FETCH failed:', err);
        replyError('UPSTREAM_ERROR', 'Fetch request failed');
      }
    }
  }

  setupMessageChannel(iframe) {
    this.cleanupChannel();
    const channel = new MessageChannel();
    this.port = channel.port1;

    this.onMessageHandler = (event) => {
      this.handlePortMessage(event, iframe);
    };
    this.port.addEventListener('message', this.onMessageHandler);
    this.port.start();

    const widgetOrigin = new URL(this.widgetHostDomain).origin;
    iframe.contentWindow.postMessage({
      type: 'WIDGET_CHANNEL_INIT',
      widgetId: this.widgetId,
      instanceId: this.instanceId,
      ownerUserId: this.ownerUserId
    }, widgetOrigin, [channel.port2]);
  }

  shouldShowWidget() {
    if (!this._flagStatus) return true;
    if (this._flagStatus.hidden) return false;
    if (this._flagStatus.gated && !this._revealed) return false;
    return true;
  }

  updateGateOverlay() {
    const gate = this.querySelector('.fot-widget-gate');
    const iframe = this.querySelector('iframe');
    if (!gate) return;

    if (!this._flagStatus) {
      gate.hidden = true;
      if (iframe) iframe.hidden = false;
      return;
    }

    if (this._flagStatus.hidden) {
      gate.hidden = false;
      gate.innerHTML = '<p class="fot-widget-gate-message">This widget is under review.</p>';
      if (iframe) iframe.hidden = true;
      return;
    }

    if (this._flagStatus.gated && !this._revealed) {
      gate.hidden = false;
      gate.innerHTML = `
        <p class="fot-widget-gate-message">This widget was flagged by another user.</p>
        <button type="button" class="btn btn-secondary btn-sm fot-widget-reveal-btn">Show anyway</button>
      `;
      gate.querySelector('.fot-widget-reveal-btn')?.addEventListener('click', async () => {
        this._revealed = true;
        const iframe = this.querySelector('iframe');
        if (iframe?.src) {
          this.updateGateOverlay();
          return;
        }
        await this.loadWidget();
      }, { once: true });
      if (iframe) iframe.hidden = true;
      return;
    }

    gate.hidden = true;
    if (iframe) iframe.hidden = false;
  }

  async refreshFlagStatus() {
    this._flagStatus = await this.fetchFlagStatus();
    this.updateGateOverlay();
  }

  async loadWidget() {
    const iframe = this.querySelector('iframe');
    if (!iframe || !this.widgetId) return;

    this._flagStatus = await this.fetchFlagStatus();
    this.updateGateOverlay();
    if (!this.shouldShowWidget()) {
      this.querySelector('.fot-widget-loading')?.remove();
      return;
    }

    let src = `${this.widgetHostDomain}/render/${encodeURIComponent(this.widgetId)}`;

    const ownerId = this.ownerUserId;
    const sectionId = this.instanceId;
    if (ownerId && sectionId) {
      try {
        const res = await fetch(
          `/api/widget-instances/${encodeURIComponent(ownerId)}/${encodeURIComponent(sectionId)}/render-token`,
          { credentials: 'include' }
        );
        if (res.ok) {
          const { token } = await res.json();
          if (token) {
            src += `?profileToken=${encodeURIComponent(token)}`;
          }
        }
      } catch (err) {
        window.logger?.warn('Widget render token fetch failed:', err);
      }
    }

    iframe.src = src;

    iframe.addEventListener('load', () => {
      try {
        this.setupMessageChannel(iframe);
      } catch (error) {
        window.logger?.error('Failed to setup widget MessageChannel:', error);
      }
    }, { once: true });

    iframe.addEventListener('error', () => {
      this.innerHTML = '<div class="fot-widget-error">Widget failed to load.</div>';
    }, { once: true });
  }

  render() {
    if (!this.widgetId) {
      this.innerHTML = '<div class="fot-widget-error">No widget specified.</div>';
      return;
    }

    this.innerHTML = `
      <div class="fot-widget">
        <div class="fot-widget-gate" hidden></div>
        <div class="fot-widget-loading loading-state"><span class="spinner"></span></div>
        <iframe
          title="Widget ${this.widgetId}"
          sandbox="allow-scripts allow-same-origin"
          loading="lazy"
        ></iframe>
      </div>
    `;
  }
}

customElements.define('fot-widget', FotWidget);
